import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import prettyBytes from "pretty-bytes";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { ComposerAttachment } from "./composerAdapters";
import { EmailComposer } from "./useEmailComposer";

const AttachmentRow = ({ attachment, disabled, onRemove }: { attachment: ComposerAttachment; disabled: boolean; onRemove: () => void }) => {
    const { t } = useOfficeTranslation();
    const [url, setUrl] = useState("");
    useEffect(() => {
        if (!attachment.blob) { setUrl(""); return; }
        const source = URL.createObjectURL(attachment.blob);
        setUrl(source);
        return () => URL.revokeObjectURL(source);
    }, [attachment.blob]);
    return <li className="composer-attachment">
        <i className="mdi mdi-file-outline" aria-hidden="true" />
        <div className="composer-attachment-copy">
            {url ? <a href={url} target="_blank" rel="noreferrer">{attachment.name}</a> : <span>{attachment.name}</span>}
            <small className={attachment.status === "error" ? "text-danger" : "text-muted"}>
                {attachment.status === "reading" ? t("email.composer.preparingAttachment")
                    : attachment.status === "error" ? t("email.composer.attachmentFailed")
                        : prettyBytes(attachment.size)}
            </small>
        </div>
        <button type="button" className="btn btn-light composer-remove" disabled={disabled} onClick={onRemove} aria-label={t("email.composer.removeAttachment", { name: attachment.name })}>
            <i className="mdi mdi-close" aria-hidden="true" />
        </button>
    </li>;
};

const ComposeAttachments = ({ composer }: { composer: EmailComposer }) => {
    const { t, intlLocale } = useOfficeTranslation();
    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const [invoiceId, setInvoiceId] = useState("");
    const { draft, sending, addFiles, loadInvoices, invoices, invoicesError, invoicesLoading, addInvoice } = composer;
    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({ onDrop: addFiles, noClick: true, noKeyboard: true, disabled: sending });
    const available = invoices.filter(value => value.customer === draft.accountId && value.tenant === draft.tenantId);
    return <section {...getRootProps()} className={`composer-attachments ${isDragActive ? "is-dragging" : ""}`} aria-label={t("email.composer.attachments")}>
        <input {...getInputProps()} aria-label={t("email.composer.addAttachment")} />
        <div className="composer-attachment-actions">
            <button type="button" className="btn btn-light" disabled={sending} onClick={open}>
                <i className="mdi mdi-paperclip me-1" aria-hidden="true" />{t("email.composer.addAttachment")}
            </button>
            {draft.accountId && <button type="button" className="btn btn-light" disabled={sending} aria-expanded={invoiceOpen} aria-controls="composer-invoice-picker" onClick={() => { setInvoiceOpen(value => !value); void loadInvoices(); }}>
                <i className="mdi mdi-file-document-outline me-1" aria-hidden="true" />{t("email.composer.addInvoice")}
            </button>}
        </div>
        {isDragActive && <p className="mb-0" role="status">{t("email.composer.dropAttachments")}</p>}
        {invoiceOpen && <div id="composer-invoice-picker" className="composer-invoice-picker">
            <label className="form-label" htmlFor="composer-invoice">{t("email.emailCompose.chooseInvoice")}</label>
            {invoicesLoading ? <p role="status">{t("email.composer.loadingInvoices")}</p>
                : invoicesError ? <div role="alert"><p className="text-danger">{t("email.composer.invoicesFailed")}</p><button type="button" className="btn btn-light" onClick={() => void loadInvoices()}>{t("email.composer.retry")}</button></div>
                    : available.length === 0 ? <p className="text-muted mb-0">{t("email.composer.noInvoices")}</p>
                        : <div className="composer-invoice-actions">
                            <select id="composer-invoice" className="form-select" value={invoiceId} disabled={sending} onChange={event => setInvoiceId(event.target.value)}>
                                <option value="">{t("email.emailCompose.chooseInvoice")}</option>
                                {available.map(value => <option key={value.id} value={value.id} disabled={draft.attachments.some(item => item.invoiceId === value.id)}>{value.GetDisplayName()} · {value.GetCreatedDate().toLocaleDateString(intlLocale)}</option>)}
                            </select>
                            <button type="button" className="btn btn-outline-primary" disabled={sending || !available.some(value => value.id === invoiceId) || draft.attachments.some(value => value.invoiceId === invoiceId)} onClick={() => {
                                const invoice = available.find(value => value.id === invoiceId);
                                if (invoice) void addInvoice(invoice);
                                setInvoiceId("");
                            }}>{t("common.attach")}</button>
                        </div>}
        </div>}
        {draft.attachments.length > 0 && <ul className="composer-attachment-list">
            {draft.attachments.map(attachment => <AttachmentRow key={attachment.id} attachment={attachment} disabled={sending} onRemove={() => composer.removeAttachment(attachment.id)} />)}
        </ul>}
    </section>;
};

export default ComposeAttachments;
